import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Plus,
  ListTodo,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { AddOnboardingTaskDialog } from '@/components/hiring/AddOnboardingTaskDialog';
import { createOnboardingTask, getOnboardingTasks, updateOnboardingTask, deleteOnboardingTask } from '@/services/onboardingTask.service';
import { checkAndFixAllTaskAssignments } from '@/services/onboarding.service';
import { ViewOnboardingTasks } from '@/components/hiring/ViewOnboardingTasks';
import { OnboardingTaskFormData, OnboardingTask } from '@/types/models';

const HiringSettings: React.FC = () => {
  const { user } = useAuthStore();

  // Permission checks
  const hiringPermissions = (user?.permissions as any)?.hiring;
  const settingsPermissions = hiringPermissions?.settings;

  // Permission checks for onboarding task management
  const canCreateOnboardingTask = settingsPermissions?.onboardingTask?.create === true;
  const canViewOnboardingTask = settingsPermissions?.onboardingTask?.view === true;

  // Permission checks for hiring settings

  // State for onboarding tasks
  const [onboardingTasks, setOnboardingTasks] = useState<OnboardingTask[]>([]);
  const [isLoadingOnboardingTasks, setIsLoadingOnboardingTasks] = useState(false);
  const [isOnboardingTaskDialogOpen, setIsOnboardingTaskDialogOpen] = useState(false);
  const [editingOnboardingTask, setEditingOnboardingTask] = useState<OnboardingTask | null>(null);

  // Fetch onboarding tasks
  const fetchOnboardingTasks = useCallback(async () => {
    try {
      setIsLoadingOnboardingTasks(true);
      const tasks = await getOnboardingTasks();
      setOnboardingTasks(tasks);
    } catch (error) {
      console.error('Error fetching onboarding tasks:', error);
      toast.error('Failed to load onboarding tasks');
    } finally {
      setIsLoadingOnboardingTasks(false);
    }
  }, []);

  useEffect(() => {
    if (canViewOnboardingTask) {
      fetchOnboardingTasks();
    }
  }, [canViewOnboardingTask, fetchOnboardingTasks]);

  // Handlers for onboarding tasks
  const handleAddOnboardingTask = async (data: OnboardingTaskFormData) => {
    try {
      await createOnboardingTask(data);
      toast.success('Onboarding task created successfully');
      fetchOnboardingTasks();
      setIsOnboardingTaskDialogOpen(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to create onboarding task');
    }
  };

  const handleEditOnboardingTask = (task: OnboardingTask) => {
    setEditingOnboardingTask(task);
    setIsOnboardingTaskDialogOpen(true);
  };

  const handleUpdateOnboardingTask = async (data: OnboardingTaskFormData) => {
    if (!editingOnboardingTask) return;
    try {
      await updateOnboardingTask(editingOnboardingTask._id, data);
      toast.success('Onboarding task updated successfully');
      fetchOnboardingTasks();
      setIsOnboardingTaskDialogOpen(false);
      setEditingOnboardingTask(null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update onboarding task');
    }
  };

  const handleDeleteOnboardingTask = async (task: OnboardingTask) => {
    try {
      const id = task._id || (task as any).id;
      if (!id) return;
      await deleteOnboardingTask(id);
      toast.success('Onboarding task deleted successfully');
      fetchOnboardingTasks();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete onboarding task');
    }
  };

  const handleCheckAndFixTaskAssignments = async () => {
    try {
      setIsLoadingOnboardingTasks(true);
      const result = await checkAndFixAllTaskAssignments();
      toast.success(result.message || 'Task assignments checked and fixed');
      fetchOnboardingTasks();
    } catch (error: any) {
      toast.error(error.message || 'Failed to check and fix task assignments');
    } finally {
      setIsLoadingOnboardingTasks(false);
    }
  };

  return (
    <div className="space-y-4 p-4 pt-1 pb-10">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Hiring Settings</h1>
        </div>
        <div className="flex items-center gap-2">
          {canCreateOnboardingTask && (
            <Button
              onClick={() => {
                setEditingOnboardingTask(null);
                setIsOnboardingTaskDialogOpen(true);
              }}
              className="flex items-center gap-2 h-9"
            >
              <Plus className="h-4 w-4" />
              Add Onboarding Task
            </Button>
          )}
          <Button
            onClick={handleCheckAndFixTaskAssignments}
            variant="outline"
            className="flex items-center gap-2 h-9"
            disabled={isLoadingOnboardingTasks}
          >
            {isLoadingOnboardingTasks ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListTodo className="h-4 w-4" />}
            Assign All Tasks
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        <ViewOnboardingTasks
          tasks={Array.isArray(onboardingTasks) ? onboardingTasks : []}
          isLoading={isLoadingOnboardingTasks}
          onRefresh={fetchOnboardingTasks}
          onEdit={handleEditOnboardingTask}
          onDelete={handleDeleteOnboardingTask}
        />
      </div>

      {/* Onboarding Task Dialog */}
      {isOnboardingTaskDialogOpen && (
        <AddOnboardingTaskDialog
          open={isOnboardingTaskDialogOpen}
          onOpenChange={(open: boolean) => {
            setIsOnboardingTaskDialogOpen(open);
            if (!open) setEditingOnboardingTask(null);
          }}
          onSubmit={editingOnboardingTask ? handleUpdateOnboardingTask : handleAddOnboardingTask}
          initialData={editingOnboardingTask || undefined}
          isEdit={!!editingOnboardingTask}
        />
      )}
    </div>
  );
};

export default HiringSettings;